import React, { useMemo, useState, useEffect } from 'react';
import Button from 'src/components/Button';
import {
  IContactAdvancedFilters,
  IndividualFilter,
} from 'src/pages/Contacts/CustomHooks/useContactAdvancedFilters';
import { toggleSelection } from 'src/pages/Contacts/ContactFilterModal/helpers';
import './ContactFilterModal.scss';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  DateRangeFilter,
  GenericFilterDropdown,
  RoleFilter,
  StatusFilter,
  TextFilter,
} from 'src/pages/Contacts/ContactFilterModal/ContcatFilterComponents/ContactFIlterComponents';
import Skeleton from 'src/components/Skeleton/Skeleton';
import {
  useContactsSuiteActionsFromContext,
  useContactsSuiteActionsDataFromContext,
} from 'src/pages/Contacts/ContactsContext/ContactsContext';
import { FetchLevelsResult } from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';

const CASCADING_DROPDOWN_COUNT: number = 4;

const DropdownSkeleton = () => (
  <div style={{ marginBottom: '1rem' }}>
    <Skeleton variant="text" width="6rem" height="1.4rem" />
    <Skeleton variant="rect" width="100%" height="3.6rem" />
  </div>
);

const CascadingDropdownsSkeleton = () => (
  <>
    {Array.from({ length: CASCADING_DROPDOWN_COUNT }).map((_, i: number) => (
      <DropdownSkeleton key={i} />
    ))}
  </>
);

interface ContactFilterModalProps {
  onClose: () => void;
  onApplyFilters: (filters: IContactAdvancedFilters) => void;
  activeFilters: IContactAdvancedFilters;
  properties: any;
  formOptions: any;
  uniqueContactStatuses: string[];
}

export const ContactFilterModal = ({
  onClose,
  onApplyFilters,
  activeFilters,
  properties,
  formOptions,
  uniqueContactStatuses,
}: ContactFilterModalProps) => {
  const authenticatedUser: any = useSelector(selectAuthenticatedUser);
  const userRole: any = authenticatedUser.roles[0]?.role?.name;
  const user: any = authenticatedUser;

  const { isLoading: isLoadingSuiteActions } =
    useContactsSuiteActionsDataFromContext();
  const {
    fetchBuildingForProperty,
    fetchPropertySuites,
    fetchLevelsForBuilding,
  } = useContactsSuiteActionsFromContext();

  // Modal state to keep track of what options the user has selected.
  const [filters, setFilters] = useState<IContactAdvancedFilters>({
    roles: activeFilters.roles || [],
    property: activeFilters.property,
    status: activeFilters.status || [],
    email: activeFilters.email || '',
    phone: activeFilters.phone || '',
    dateRange: {
      startDate: activeFilters.dateRange?.startDate || null,
      endDate: activeFilters.dateRange?.endDate || null,
    },
    name: activeFilters.name || '',
    selectedUnit: activeFilters.selectedUnit,
    selectedBuilding: activeFilters.selectedBuilding,
    selectedLevel: activeFilters.selectedLevel,
  });

  // State for validation errors.
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);

  // State to keep track of the users selected buildings and suites dropdown menus.
  const [suitesForUI, setSuitesForUI] = useState<any[] | null>(null);
  const [buildingsForUI, setBuildingsForUI] = useState<any[] | null>(null);
  const [levelsForUI, setLevelsForUI] = useState<any[] | null>(null);
  const [isSyncingCascadingDropdownData, setIsSyncingCascadingDropdownData] =
    useState(false);

  // Validate date range whenever dateRange changes
  useEffect(() => {
    validateDateRange();
  }, [filters.dateRange?.startDate, filters.dateRange?.endDate]);

  const restoreDropdownData = async () => {
    if (activeFilters.property && activeFilters.property.length > 0) {
      const selectedPropertyId = activeFilters.property[0].Id;

      if (selectedPropertyId && user?.id && userRole) {
        try {
          const [buildingsResult, suitesResult] = await Promise.all([
            fetchBuildingForProperty(selectedPropertyId),
            fetchPropertySuites(selectedPropertyId, user.id, userRole),
          ]);

          setBuildingsForUI(buildingsResult.buildings);
          setSuitesForUI(suitesResult.suites);

          if (
            activeFilters.selectedBuilding &&
            activeFilters.selectedBuilding.length > 0
          ) {
            const selectedBuildingId = activeFilters.selectedBuilding[0].Id;

            if (selectedBuildingId) {
              const levelsResult =
                await fetchLevelsForBuilding(selectedBuildingId);
              setLevelsForUI(levelsResult.levels);
            }
          }
        } catch (error) {
          console.error('Error restoring dropdown data:', error);
        }
      }
    }
  };

  // When modal opens with active filters, restore the dropdown data
  useEffect(() => {
    // Only run this when the modal first opens with active filters
    if (Object.keys(activeFilters).length > 0) {
      restoreDropdownData();
    }
  }, []); // Empty dependency array so it only runs once when component mounts

  // Function to validate date range
  const validateDateRange = () => {
    // Reset errors first.
    setStartDateError(null);
    setEndDateError(null);
    setIsValid(true);

    const { startDate, endDate } = filters.dateRange || {};

    // If both dates are set, check if start date is after end date.
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setStartDateError('Start date cannot be after end date');
        setEndDateError('End date cannot be before start date');
        setIsValid(false);
        return false;
      }
    }

    return true;
  };

  // Get available roles from formOptions.
  const availableRoles: string[] = useMemo(() => {
    const hasFormOptions: boolean = formOptions && formOptions.allRoles;

    if (hasFormOptions) {
      return formOptions.allRoles.map((role: any): any => role.name);
    }

    // Default roles if form options are not available.
    return [
      'Property Developer',
      'Service Provider',
      'Home Owner',
      'Realtor',
      'Vendor',
      'Strata Manager',
      'Occupant',
      'Service Manager',
    ];
  }, [formOptions]);

  const handleRoleChange = (role: string) => {
    const updatedRoles: string[] = toggleSelection(filters.roles, role);

    setFilters({
      ...filters,
      roles: updatedRoles,
    });
  };

  // Helper function to reset all downstream state from property level
  const resetAllDownstreamState = () => {
    setBuildingsForUI(null);
    setLevelsForUI(null);
    setSuitesForUI(null);
    setFilters((prev) => ({
      ...prev,
      selectedBuilding: null,
      selectedLevel: null,
      selectedUnit: null,
    }));
  };

  // Helper function to reset level downstream state
  const resetLevelDownstreamState = () => {
    setLevelsForUI(null);
    setFilters((prev) => ({
      ...prev,
      selectedLevel: null,
    }));
  };

  const handlePropertyChange = async (selectedProperty: IndividualFilter[]) => {
    // Check if this is a reset action
    if (!selectedProperty || selectedProperty.length === 0) {
      setFilters({
        ...filters,
        property: null,
        selectedBuilding: null,
        selectedLevel: null,
        selectedUnit: null,
      });

      resetAllDownstreamState();
      return;
    }

    setFilters({
      ...filters,
      property: selectedProperty,
      // Reset all downstream selections when property changes
      selectedBuilding: null,
      selectedLevel: null,
      selectedUnit: null,
    });

    resetAllDownstreamState();

    const selectedPropertyId: string = selectedProperty[0]?.Id;

    if (selectedPropertyId && user?.id && userRole) {
      try {
        setIsSyncingCascadingDropdownData(true);

        const [buildingsResult, suitesResult] = await Promise.all([
          fetchBuildingForProperty(selectedPropertyId),
          fetchPropertySuites(selectedPropertyId, user.id, userRole),
        ]);

        setBuildingsForUI(buildingsResult.buildings);
        setSuitesForUI(suitesResult.suites);
      } catch (error) {
        console.error('Error fetching property data:', error);
      } finally {
        setIsSyncingCascadingDropdownData(false);
      }
    }
  };

  const handleBuildingChange = async (selectedBuilding: any) => {
    // Check if this is a reset action
    if (!selectedBuilding || selectedBuilding.length === 0) {
      setFilters({
        ...filters,
        selectedBuilding: null,
        selectedLevel: null,
      });

      resetLevelDownstreamState();
      return;
    }

    setFilters({
      ...filters,
      selectedBuilding: selectedBuilding,
      // Reset level selection when building changes.
      selectedLevel: null,
    });

    resetLevelDownstreamState();

    const selectedBuildingId: string = selectedBuilding[0]?.Id;

    if (selectedBuildingId) {
      try {
        setIsSyncingCascadingDropdownData(true);
        const levelsResult: FetchLevelsResult =
          await fetchLevelsForBuilding(selectedBuildingId);
        setLevelsForUI(levelsResult.levels);
      } catch (error) {
        console.error('Error fetching levels:', error);
      } finally {
        setIsSyncingCascadingDropdownData(false);
      }
    }
  };

  const handleLevelChange = async (selectedLevel: any) => {
    if (!selectedLevel || selectedLevel.length === 0) {
      setFilters({
        ...filters,
        selectedLevel: null,
      });

      return;
    }

    setFilters({
      ...filters,
      selectedLevel: selectedLevel,
    });
  };

  const handleUnitChange = async (selectedUnit: any) => {
    // Check if this is a reset action
    if (!selectedUnit || selectedUnit.length === 0) {
      setFilters({
        ...filters,
        selectedUnit: null,
      });
      return;
    }

    setFilters({
      ...filters,
      selectedUnit: selectedUnit,
    });
  };

  const handleStatusChange = (status: string) => {
    const updatedStatuses: string[] = toggleSelection(filters.status, status);

    setFilters({
      ...filters,
      status: updatedStatuses,
    });
  };

  // Handle date change for date range filter.
  const handleDateChange = (
    field: 'startDate' | 'endDate',
    value: Date | null
  ) => {
    const updatedDateRange = {
      ...filters.dateRange,
      [field]: value,
    };

    setFilters({
      ...filters,
      dateRange: updatedDateRange,
    });
  };

  const handleTextFilterChange = (
    field: 'email' | 'phone' | 'name',
    value: string
  ) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleSubmit = () => {
    if (validateDateRange()) {
      onApplyFilters(filters);
      onClose();
    }
  };

  return (
    <div className="filter-modal-contacts">
      {/* Role Filter. */}
      <RoleFilter
        roles={availableRoles}
        selectedRoles={filters.roles || []}
        onRoleChange={handleRoleChange}
      />
      {properties.length > 0 &&
        (isLoadingSuiteActions || isSyncingCascadingDropdownData ? (
          <CascadingDropdownsSkeleton />
        ) : (
          <>
            <GenericFilterDropdown
              title="Property"
              id="properties"
              data={properties}
              selectedValue={filters.property || []}
              onChange={handlePropertyChange}
              idField="propertyId"
              nameField="name"
              placeholder="Select Property"
              showResetOption={true}
              resetLabel="Reset Property Selection"
            />
            <GenericFilterDropdown
              title="Building"
              id="buildings"
              data={buildingsForUI}
              selectedValue={filters.selectedBuilding}
              onChange={handleBuildingChange}
              idField="id"
              nameField="building"
              placeholder={
                !filters.property?.length
                  ? 'Select Property first'
                  : !buildingsForUI?.length
                    ? 'No buildings available'
                    : 'Select Building'
              }
              disabled={!filters.property?.length || !buildingsForUI?.length}
              showResetOption={!!filters.selectedBuilding}
              resetLabel="Reset Building Selection"
            />
            <GenericFilterDropdown
              title="Unit"
              id="units"
              data={suitesForUI}
              selectedValue={filters.selectedUnit}
              onChange={handleUnitChange}
              idField="id"
              nameField="number"
              placeholder={
                !filters.property?.length
                  ? 'Select Property first'
                  : !suitesForUI?.length
                    ? 'No units available'
                    : 'Select Unit'
              }
              disabled={!filters.property?.length || !suitesForUI?.length}
              showResetOption={!!filters.selectedUnit}
              resetLabel="Reset Unit Selection"
            />
            <GenericFilterDropdown
              title="Level"
              id="levels"
              data={levelsForUI}
              selectedValue={filters.selectedLevel}
              onChange={handleLevelChange}
              idField="id"
              nameField="level"
              placeholder={
                !filters.selectedBuilding?.length
                  ? 'Select Building first'
                  : !levelsForUI?.length
                    ? 'No levels available'
                    : 'Select Level'
              }
              disabled={
                !filters.selectedBuilding?.length || !levelsForUI?.length
              }
              showResetOption={!!filters.selectedLevel}
              resetLabel="Reset Level Selection"
            />
          </>
        ))}
      {/* Status Filter. */}
      <StatusFilter
        statuses={uniqueContactStatuses}
        selectedStatuses={filters.status || []}
        onStatusChange={handleStatusChange}
      />
      {/* Email Filter. */}
      <TextFilter
        title="Email"
        value={filters.email || ''}
        placeholder="Filter by email..."
        onChange={(value: string) => handleTextFilterChange('email', value)}
      />
      {/* Name Filter. */}
      <TextFilter
        title="Name"
        value={filters.name || ''}
        placeholder="Filter by name..."
        onChange={(value: string) => handleTextFilterChange('name', value)}
      />
      {/* Phone Filter. */}
      <TextFilter
        title="Phone"
        value={filters.phone || ''}
        placeholder="Filter by phone..."
        onChange={(value: string) => handleTextFilterChange('phone', value)}
      />
      {/* Date Range Filter with validation. */}
      <DateRangeFilter
        dateRange={filters.dateRange || { startDate: null, endDate: null }}
        onDateChange={handleDateChange}
        startDateError={startDateError}
        endDateError={endDateError}
      />
      {/* Action Buttons. */}
      <div style={{ textAlign: 'center' }}>
        <Button
          color="teal"
          label="Submit"
          size="small"
          onClick={handleSubmit}
          disabled={!isValid}
        />
      </div>
    </div>
  );
};

interface ContactFilterOverlayProps {
  showFilterModal: boolean;
  setShowFilterModal: (show: boolean) => void;
  onApplyFilters: (filters: IContactAdvancedFilters) => void;
  activeFilters: IContactAdvancedFilters;
  properties: any;
  formOptions: any;
  uniqueContactStatuses: string[];
}

export const ContactFilterOverlay = ({
  showFilterModal,
  setShowFilterModal,
  onApplyFilters,
  activeFilters,
  properties,
  formOptions,
  uniqueContactStatuses,
}: ContactFilterOverlayProps) => {
  return (
    <GenericDialogue
      isOpen={showFilterModal}
      onClose={() => setShowFilterModal(false)}
      title="Filter Contacts"
      description="Refine your contact list with the options below"
    >
      <ContactFilterModal
        onClose={() => setShowFilterModal(false)}
        onApplyFilters={onApplyFilters}
        activeFilters={activeFilters}
        properties={properties}
        formOptions={formOptions}
        uniqueContactStatuses={uniqueContactStatuses}
      />
    </GenericDialogue>
  );
};
